import { CMAWagerEvent } from "../../api/common/model/data/BalanceManager";
import { LoaderEvent } from "../../loader/event/LoaderEvent";
import { KeyValuePair } from "../../util/collection/KeyValuePair";
import { EventObject } from "../../util/event/EventObject";
import { AppEvent, GameErrorEvent } from "../Constants";
import { DialogData } from "../control/data/DialogData";
import { FullScreenManager } from "../manager/FullScreenManager";
import { LanguageManager } from "../manager/LanguageManager";
import { RealityCheckDialog } from "../view/display/RealityCheckDialog";
import { ResponsibleGamingDialog } from "../view/display/ResponsibleGamingDialog";
import { CommonLang } from "../view/lang/CommonLang";
import { AbstractGameController } from "./AbstractGameController";
import { CommonGameEvent } from "./event/CommonGameEvent";
/**
 * AbstractDialogController.ts
 */
export class AbstractDialogController extends AbstractGameController {
    //IMPORTANT! do not change any function names in this class. Dialog callbacks
    //are called by string name and not by reference. If you change a function name
    //you will need to update the string reference in the dialog data object.
    //see errorCodes.json in js-localisation project
    //https://bitbucket.org/gamingrealms1/js-localisation/src/master/
    constructor(factory) {
        super(factory);
        this.operatorConfig = this.baseFactory.getOperatorConfig();
    }
    createModel() {
        super.createModel();
        this.baseModel = this.baseFactory.getModel();
    }
    mapEvents(events, callback) {
        events.forEach(event => {
            this.registerCore(event, callback);
        });
    }
    addEventListeners() {
        super.addEventListeners();
        //ResultCode.UNKNOWN_PROMOTION_CODE, ResultCode.GAME_ACCESS_DENIED
        this.mapEvents([AppEvent.ERROR, GameErrorEvent.UNKNOWN_PROMOTION_CODE, GameErrorEvent.GAME_ACCESS_DENIED], () => {
            let data = this.createDialog(CommonLang.ERROR_TITLE, CommonLang.ERROR_MESSAGE, this.handleNonRecoverableErrorComplete);
            this.openDialog(this.baseProperties.messageDialogProperties, data);
        });
        this.mapEvents([GameErrorEvent.ACCOUNT_LIMIT_EXCEEDED_GENERIC, GameErrorEvent.RESPONSIBLE_GAMING_TIME_LIMIT], () => {
            let data = this.createDialog(CommonLang.ACCOUNT_LIMIT_EXCEEDED_GENERIC_TITLE, CommonLang.ACCOUNT_LIMIT_EXCEEDED_GENERIC_MESSAGE, this.handleErrorComplete);
            this.openDialog(this.baseProperties.messageDialogProperties, data);
        });
        this.registerCore(GameErrorEvent.CURRENCY_NOT_SUPPORTED, () => {
            let data = this.createDialog(CommonLang.CURRENCY_NOT_SUPPORTED_TITLE, CommonLang.CURRENCY_NOT_SUPPORTED_MESSAGE, this.handleExitGame);
            this.openDialog(this.baseProperties.messageDialogProperties, data);
        });
        this.registerCore(GameErrorEvent.ACCOUNT_LIMIT_EXCEEDED, () => {
            let data = this.createDialog(CommonLang.ACCOUNT_LIMIT_EXCEEDED_TITLE, CommonLang.ACCOUNT_LIMIT_EXCEEDED_MESSAGE, this.handleErrorComplete);
            this.openDialog(this.baseProperties.messageDialogProperties, data);
        });
        this.registerCore(GameErrorEvent.WAGER_CASH, () => {
            let data = this.createDialog(CommonLang.WAGER_CASH_TITLE, CommonLang.WAGER_CASH_MESSAGE, this.handleWagerConfirm);
            this.openDialog(this.baseProperties.messageDialogProperties, data);
        });
        this.registerCore(GameErrorEvent.RESPONSIBLE_GAMING_LIMIT, () => {
            if (this.responsibleGaming.hasTrigger()) {
                let trigger = this.responsibleGaming.getTrigger();
                let messageKey = CommonLang.RESPONSIBLE_GAMING_MESSAGE_PREFIX + trigger.getId();
                let data = this.createDialog(CommonLang.RESPONSIBLE_GAMING_TITLE, messageKey, this.handleErrorComplete);
                this.dialogManager.open(this.baseProperties.messageDialogProperties, undefined, data);
            }
        });
        this.registerCore(CMAWagerEvent.WAGER_CASH, () => {
            let data = this.createDialog(CommonLang.WAGER_CASH_TITLE, CommonLang.WAGER_CASH_MESSAGE);
            this.openDialog(this.baseProperties.messageDialogProperties, data);
        });
        this.registerCore(GameErrorEvent.RESPONSIBLE_GAMING_TURNOVER_LIMIT, () => {
            let data = this.createDialog(CommonLang.RESPONSIBLE_GAMING_TURNOVER_LIMIT_TITLE, CommonLang.RESPONSIBLE_GAMING_TURNOVER_LIMIT_MESSAGE, this.handleErrorComplete);
            this.openDialog(this.baseProperties.messageDialogProperties, data);
        });
        //done
        this.registerCore(GameErrorEvent.RESPONSIBLE_GAMING_TIME_LIMIT, () => {
            let data = this.createDialog(CommonLang.RESPONSIBLE_GAMING_TIME_LIMIT_TITLE, CommonLang.RESPONSIBLE_GAMING_TIME_LIMIT_MESSAGE, this.handleErrorComplete);
            this.openDialog(this.baseProperties.messageDialogProperties, data);
        });
        //done
        this.registerCore(GameErrorEvent.RESTRICTED_TERRITORY, () => {
            let data = this.createDialog(CommonLang.RESTRICTED_TERRITORY_TITLE, CommonLang.RESTRICTED_TERRITORY_MESSAGE, this.handleExitGame);
            this.openDialog(this.baseProperties.messageDialogProperties, data);
        });
        this.registerGame(CommonGameEvent.GAME_OVER_CONFIRM, () => {
            let data = new DialogData();
            data.addLang(CommonLang.DIALOG_MESSAGE, this.languageManager.getLang(CommonLang.GAME_OVER_CONFIRM_MESSAGE));
            data.onConfirm(this.handleGameOverConfirmed, this);
            this.dialogManager.open(this.baseProperties.messageDialogProperties, undefined, data);
        });
        this.registerGame(CommonGameEvent.MAX_PURCHASE_STAKE_EXCEEDED, () => {
            let data = new DialogData();
            data.addLang(CommonLang.DIALOG_MESSAGE, this.languageManager.getLang(CommonLang.PURCHASE_STAKE_EXCEEDED_MESSAGE));
            data.onConfirm(this.handleGameOverConfirmed, this);
            this.dialogManager.open(this.baseProperties.messageDialogProperties, undefined, data);
        });
        this.registerGame(CommonGameEvent.MAX_TOTAL_STAKE_EXCEEDED, () => {
            let data = new DialogData();
            data.addLang(CommonLang.DIALOG_MESSAGE, this.languageManager.getLang(CommonLang.TOTAL_STAKE_EXCEEDED_MESSAGE));
            data.onConfirm(this.handleGameOverConfirmed, this);
            this.dialogManager.open(this.baseProperties.messageDialogProperties, undefined, data);
        });
        this.registerCore(AppEvent.DYNAMIC_POPUP, (event) => {
            let payload = event.getPayload();
            if (payload) {
                let displayData = payload;
                if (displayData.title && displayData.message) {
                    let confirmFnctn = null;
                    if (displayData.confirmAction) {
                        confirmFnctn = this[displayData.confirmAction];
                        if (!confirmFnctn) {
                            console.warn("Function not found: " + displayData.confirmAction);
                        }
                    }
                    let cancelFnctn = null;
                    if (displayData.cancelAction) {
                        cancelFnctn = this[displayData.cancelAction];
                        if (!cancelFnctn) {
                            console.warn("Function not found: " + displayData.cancelAction);
                        }
                    }
                    let data = this.createDialog(displayData.title, displayData.message, confirmFnctn, cancelFnctn, displayData.timeout);
                    this.dialogManager.open(this.baseProperties.messageDialogProperties, undefined, data);
                }
            }
        });
        this.mapEvents([GameErrorEvent.USER_NOT_AUTHENTICATED, GameErrorEvent.SESSION_TIME_OUT], () => {
            let fnctn = null;
            if (this.operatorConfig.isExitOnSessionEnabled()) {
                fnctn = this.handleErrorComplete;
            }
            this.dispatchCore(new EventObject(CommonGameEvent.BI_ERROR.getType(), {
                category: "TITAN_ERROR",
                operation: "SESSION_TIME_OUT",
                value: null
            }));
            let data = this.createDialog(CommonLang.SESSION_TIME_OUT_TITLE, CommonLang.SESSION_TIME_OUT_MESSAGE, fnctn);
            this.openDialog(this.baseProperties.messageDialogProperties, data);
        });
        this.registerCore(LoaderEvent.COMPLETE, () => {
            this.dialogManager.renderable = false;
        });
        //ResultCode.INSUFFICIENT_FUNDS || ResultCode.INSUFFICIENT_BALANCE
        this.registerCore(GameErrorEvent.INSUFFICIENT_FUNDS, () => {
            if (this.responsibleGaming.isEnabled()) {
                let data = this.createDialog(CommonLang.INSUFFICIENT_FUNDS_TITLE, CommonLang.INSUFFICIENT_FUNDS_RESPONSIBLE_GAMING_MESSAGE, this.handleErrorComplete);
                this.openDialog(this.baseProperties.messageDialogProperties, data);
            }
            else if (this.operatorConfig.isDepositEnabled()) {
                let data = this.createDialog(CommonLang.INSUFFICIENT_FUNDS_TITLE, CommonLang.INSUFFICIENT_FUNDS_DEPOSIT_MESSAGE, this.handleInsufficientFundsConfirm, this.handleErrorComplete);
                this.openDialog(this.baseProperties.promptDialogProperties, data);
            }
            else {
                let data = this.createDialog(CommonLang.INSUFFICIENT_FUNDS_TITLE, CommonLang.INSUFFICIENT_FUNDS_NO_DEPOSIT_MESSAGE, this.handleErrorComplete);
                this.openDialog(this.baseProperties.messageDialogProperties, data);
            }
        });
        this.registerCore(GameErrorEvent.REALITY_CHECK, () => {
            if (this.operatorConfig.isRealityCheckOptionsEnabled()) {
                this.handleRealityCheckOptions();
            }
            else {
                this.handleRealityCheckNoOptions();
            }
        });
        this.registerCore(GameErrorEvent.WAGER_BONUS, () => {
            let data = this.createDialog(CommonLang.WAGER_BONUS_TITLE, CommonLang.WAGER_BONUS_MESSAGE, this.handleWagerConfirm, this.handleWagerCancel);
            data.addButtonLang(DialogData.CANCEL, CommonLang.TERMS_AND_CONDITIONS);
            data.addButtonLang(DialogData.CONFIRM, CommonLang.OK_BUTTON);
            this.openDialog(this.baseProperties.promptDialogProperties, data);
        });
        this.registerCore(GameErrorEvent.RESPONSIBLE_GAMING_PRICE_THRESHOLD, () => {
            let data = new DialogData();
            let params = [new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(this.responsibleGaming.getSpinPrice()))];
            data.addLang(CommonLang.DIALOG_TITLE, this.languageManager.getLang(CommonLang.RESPONSIBLE_GAMING_SPIN_PRICE_THRESHOLD_TITLE));
            data.addLang(CommonLang.DIALOG_MESSAGE, this.languageManager.getLang(CommonLang.RESPONSIBLE_GAMING_SPIN_PRICE_THRESHOLD_MESSAGE, params));
            data.onCancel(this.handlePurchaseSpinCancel, this);
            data.onConfirm(this.handlePurchaseSpinConfirm, this);
            this.dialogManager.open(this.baseProperties.promptDialogProperties, undefined, data);
        });
        this.registerCore(CMAWagerEvent.WAGER_BONUS, () => {
            let data = this.createDialog(CommonLang.WAGER_BONUS_TITLE, CommonLang.WAGER_BONUS_MESSAGE, null, this.handleWagerCancel);
            data.addButtonLang(DialogData.CANCEL, CommonLang.TERMS_AND_CONDITIONS);
            data.addButtonLang(DialogData.CONFIRM, CommonLang.OK_BUTTON);
            this.openDialog(this.baseProperties.promptDialogProperties, data);
        });
        this.registerGame(CommonGameEvent.RESPONSIBLE_GAMING_OPEN, () => {
            let data = new DialogData();
            data.addParam(ResponsibleGamingDialog.STAKE, this.baseModel.getCurrentStake());
            data.addParam(ResponsibleGamingDialog.RESPONSIBLE_GAMING, this.baseModel.getResponsibleGaming());
            data.onCancel(() => {
                this.baseModel.dispatchKPI("TITAN_STATE", "PLAY_CONTROLS_CANCELLED", null);
                this.dispatchGame(CommonGameEvent.RESPONSIBLE_GAMING_CANCEL_SETTINGS);
            }, this);
            data.onConfirm(() => {
                this.dispatchGame(CommonGameEvent.RESPONSIBLE_GAMING_SAVE_SETTINGS);
            }, this);
            this.dialogManager.open(this.baseProperties.responsibleGamingDialogProperties, undefined, data);
        });
        this.registerGame(CommonGameEvent.RESPONSIBLE_GAMING_CLOSE, () => {
            this.dialogManager.close();
        });
        this.registerGame(CommonGameEvent.CLOSE_CURRENT_DIALOG, () => {
            this.dialogManager.close();
        }); //Hide dialogManager when the loader has finished successfully, but is yet to be dismissed
        this.registerCore(LoaderEvent.LOADER_REMOVE, () => {
            this.dialogManager.renderable = true;
            this.dispatchGame(CommonGameEvent.RESPONSIBLE_GAMING_INSTRUCTIONS_CLOSE);
        });
    }
    create() {
        super.create();
        this.createLanguageManager();
        this.createDialogManager();
        this.createFullScreenManager();
        this.createProperties();
    }
    createLanguageManager() {
        this.languageManager = LanguageManager.getInstance();
    }
    createDialogManager() {
        this.dialogManager = this.baseFactory.getDialogManager();
    }
    createFullScreenManager() {
        this.fullScreenManager = FullScreenManager.getInstance();
    }
    createProperties() {
        this.baseProperties = this.dialogManager.getProperties();
    }
    preload() {
        if (this.responsibleGaming.isEnabled()) {
            this.dispatchGame(CommonGameEvent.RESPONSIBLE_GAMING_INSTRUCTIONS_OPEN);
            let data = new DialogData();
            data.addParam(ResponsibleGamingDialog.STAKE, this.baseModel.getCurrentStake());
            data.addParam(ResponsibleGamingDialog.RESPONSIBLE_GAMING, this.responsibleGaming);
            this.preloadDialog(this.baseProperties.responsibleGamingDialogProperties, undefined, data);
        }
    }
    preloadDialog(properties, portraitProperties, data) {
        if (properties) {
            this.dialogManager.preload(properties, portraitProperties, data);
        }
    }
    createDialog(titleKey, msgKey, confirmFnctn, cancelFnctn, timeout = 0) {
        let data = new DialogData();
        data.addLang(CommonLang.DIALOG_TITLE, this.languageManager.getLang(titleKey));
        data.addLang(CommonLang.DIALOG_MESSAGE, this.languageManager.getLang(msgKey));
        if (confirmFnctn) {
            data.onConfirm(confirmFnctn, this);
        }
        if (cancelFnctn) {
            data.onCancel(cancelFnctn, this);
        }
        if (timeout > 0) {
            data.setTimeout(timeout);
        }
        return data;
    }
    handleRealityCheckOptions() {
        let data = new DialogData();
        data.addLang(CommonLang.DIALOG_TITLE, this.languageManager.getLang(CommonLang.REALITY_CHECK_TITLE));
        data.addLang(CommonLang.DIALOG_MESSAGE, this.languageManager.getLang(CommonLang.REALITY_CHECK_OPTIONS_MESSAGE, this.getRealityCheckParams()));
        if (this.operatorConfig.isWinLossEnabled()) {
            data.addLang(CommonLang.REALITY_CHECK_WIN, this.languageManager.getLang(CommonLang.REALITY_CHECK_WIN, this.getRealityCheckWinParam()));
            data.addLang(CommonLang.REALITY_CHECK_WAGERED, this.languageManager.getLang(CommonLang.REALITY_CHECK_WAGERED, this.getRealityCheckWageredParam()));
        }
        data.onEvent(RealityCheckDialog.EXIT_GAME, this.handleExitGame, this);
        data.onEvent(RealityCheckDialog.WAGER_HISTORY, () => {
            this.dispatchCore(AppEvent.BET_HISTORY);
        }, this);
        this.openDialog(this.baseProperties.realityCheckDialogProperties, data, true);
    }
    handleRealityCheckNoOptions() {
        let data = new DialogData();
        data.addButtonLang(DialogData.CONFIRM, CommonLang.CONTINUE_BUTTON);
        data.addLang(CommonLang.DIALOG_TITLE, this.languageManager.getLang(CommonLang.REALITY_CHECK_TITLE));
        data.addLang(CommonLang.DIALOG_MESSAGE, this.languageManager.getLang(CommonLang.REALITY_CHECK_NO_OPTIONS_MESSAGE, this.getRealityCheckParams()));
        this.openDialog(this.baseProperties.realityCheckNoOptionsDialogProperties, data, true);
    }
    handleRecovery() {
        let data = this.createDialog(CommonLang.RECOVERY_TITLE, CommonLang.RECOVERY_MESSAGE);
        this.openDialog(this.baseProperties.messageDialogProperties, data);
        this.dispatchGame(CommonGameEvent.RECOVERY);
    }
    handlePurchaseSpinConfirm() {
        this.responsibleGaming.setPriceConfirm(true);
        this.handleErrorComplete();
    }
    handlePurchaseSpinCancel() {
        this.responsibleGaming.setPriceCancel(true);
        this.handleErrorComplete();
    }
    handleWagerCancel() {
        this.dispatchCore(AppEvent.BONUS);
        this.dispatchCore(AppEvent.ERROR_COMPLETE);
    }
    handleWagerConfirm() {
        let data = this.baseModel.getErrorData();
        data.setAcknowledged(true);
        this.dispatchCore(AppEvent.ERROR_COMPLETE);
    }
    handleInsufficientFundsConfirm() {
        this.handleDeposit();
        this.handleExitGame();
    }
    handleGameOverConfirmed() {
        this.dispatchGame(CommonGameEvent.GAME_OVER_CONFIRMED);
    }
    handleNonRecoverableErrorComplete() {
        this.dispatchCore(AppEvent.NON_RECOVERABLE_ERROR_COMPLETE);
    }
    handleErrorComplete() {
        this.dispatchCore(AppEvent.ERROR_COMPLETE);
    }
    handleExitGame() {
        this.baseModel.dispatchKPI("TITAN_STATE", "DIALOG_EXIT_GAME", null);
        this.dispatchCore(AppEvent.EXIT_GAME);
    }
    handleDeposit() {
        this.dispatchCore(AppEvent.DEPOSIT);
    }
    getRealityCheckParams() {
        let details = this.baseModel.getRealityCheckDetails();
        let totalSessionTime = details ? details.getTotalSessionTime() : 0;
        let inaccurateTimerBuffer = 10000;
        let minutes = Math.floor((totalSessionTime + inaccurateTimerBuffer) / 60000);
        return [new KeyValuePair(CommonLang.PARAM_AMOUNT, minutes.toString())];
    }
    getRealityCheckWinParam() {
        let details = this.baseModel.getWinLoss();
        let win = details ? details.getTotalWin() : 0;
        let rgsParameters = this.baseFactory.getWrapperConfig().getOperatorParameters();
        let sessionWin = rgsParameters.getTotalSessionWin();
        if (sessionWin) {
            let sessionWinValue = parseFloat(sessionWin);
            win = win + sessionWinValue;
        }
        return [new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(win))];
    }
    getRealityCheckWageredParam() {
        let details = this.baseModel.getWinLoss();
        let wagered = details ? details.getTotalLoss() : 0;
        let rgsParameters = this.baseFactory.getWrapperConfig().getOperatorParameters();
        let sessionLoss = rgsParameters.getTotalSessionLoss();
        if (sessionLoss) {
            let sessionLossValue = parseFloat(sessionLoss);
            wagered = wagered + sessionLossValue;
        }
        return [new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(wagered))];
    }
    openDialog(properties, data, refresh) {
        if (this.operatorConfig.isDialogEnabled()) {
            this.dialogManager.open(properties, undefined, data, refresh);
        }
    }
}
//# sourceMappingURL=AbstractDialogController.js.map